import { CampusName } from '@/components/common/output/campus';
import { MemberStatusLabel } from '@/components/common/output/memberstatus';
import type { Column } from '@/components/common/table';
import type { Person } from '@/store/people/types';
import { getGroupAlias } from '../../structure/utils';
import { GroupName } from '@/components/common/output/group';
import { AddressCity } from '@/components/common/output/address';
import { type PeopleAccess } from '@/shared/types/policy';
import { type MinistryTypeKey } from '@/shared/types/ministry';
import { HouseholdCell } from './household';
import { PersonActions } from './actions';
import { GenderDisplay } from '@/components/common/input/gender';

export type ColumnKey =
  | 'phone'
  | 'fullname'
  | 'email'
  | 'city'
  | 'household'
  | 'gender'
  | 'campus'
  | 'member_status'
  | 'laygroup1'
  | 'laygroup2'
  | 'actions';

export interface PersonColumn extends Column<Person> {
  key: ColumnKey;
  ministries?: MinistryTypeKey[];
}

export type ActionKey =
  | 'edit'
  | 'tags'
  | 'view'
  | 'view-household'
  | 'comment'
  | 'reminder'
  | 'milestones';

interface ColumnParams {
  item: Person;
  column: Column<Person>;
}

export function makeColumns(
  onAction: (item: Person, key: ActionKey) => void,
  access: PeopleAccess,
  ministry: MinistryTypeKey | '',
): PersonColumn[] {
  const columns: PersonColumn[] = [
    {
      key: 'fullname',
      label: 'Name',
      display: ({ item }: ColumnParams) => <div>{item.data.fullname}</div>,
    },
    {
      key: 'email',
      label: 'Email',
      display: ({ item }: ColumnParams) => <div>{item.data.email || ''}</div>,
    },
    {
      key: 'phone',
      label: 'Phone',
      display: ({ item }: ColumnParams) => <div>{item.data.phone || ''}</div>,
    },
    {
      key: 'gender',
      label: 'Gender',
      display: ({ item }: ColumnParams) => (
        <div className='text-center text-xs'>
          <GenderDisplay
            value={item.data.gender}
            variant='standard'
            className='text-sm'
          />
        </div>
      ),
    },
    {
      key: 'household',
      label: 'Household',
      display: ({ item }: ColumnParams) => (
        <HouseholdCell
          id={item.data.household_id}
          onClick={() => onAction(item, 'view-household')}
        />
      ),
    },
    {
      key: 'city',
      label: 'City',
      display: ({ item }: ColumnParams) => (
        <AddressCity data={item.data.address} />
      ),
    },
    {
      key: 'campus',
      label: 'Campus',
      display: ({ item }: ColumnParams) => (
        <CampusName id={item.data.campus_id || ''} />
      ),
    },
    {
      key: 'member_status',
      label: 'Member Status',
      display: ({ item }: ColumnParams) => (
        <MemberStatusLabel id={item.data.member_status || ''} />
      ),
    },
    {
      key: 'laygroup1',
      label: getGroupAlias('laygroup1', 'type'),
      display: ({ item }: { item: Person }) => (
        <GroupName id={item.data.groups?.laygroup1} />
      ),
      ministries: ['pastoral_care'],
    },
    {
      key: 'laygroup2',
      label: getGroupAlias('laygroup2', 'type'),
      display: ({ item }: { item: Person }) => (
        <GroupName id={item.data.groups?.laygroup2} />
      ),
      ministries: ['pastoral_care'],
    },
    {
      key: 'actions',
      label: '',
      display: ({ item }: { item: Person }) => (
        <PersonActions item={item} onAction={onAction} access={access} />
      ),
    },
  ];

  return columns.filter(
    (col) => !col.ministries || (ministry && col.ministries.includes(ministry)),
  );
}
